import { createApp, configureCompat } from 'vue';
import { createHead, VueHeadMixin } from '@unhead/vue';
import Vddl from 'vddl';
import Hammer from 'hammerjs';
import propagating from 'propagating-hammerjs';
import VueProgressiveImage from 'vue-progressive-image';
import 'vue-progressive-image/dist/style.css';
import Echo from 'laravel-echo';
import io from 'socket.io-client';
import VueGtag from 'vue-gtag';
import FloatingVue from 'floating-vue';
import { VueQueryPlugin } from '@tanstack/vue-query';
import queryClient from './queries/queryClient.js';

import router from './router/index.js';
import store from './vuex/store.js';
import PageView from './components/all/PageView.vue';

import 'floating-vue/dist/style.css';

import '../sass/style.scss';
import QueryObservers from './queries/observers.js';

configureCompat({
  MODE: 3,
  INSTANCE_CHILDREN: true,
  RENDER_FUNCTION: false,
  INSTANCE_EVENT_HOOKS: true,
  INSTANCE_EVENT_EMITTER: true,
  GLOBAL_SET: true,
  GLOBAL_DELETE: true,
  ATTR_ENUMERATED_COERCION: true,
  INSTANCE_SET: true,
});

// eslint-disable-next-line vue/one-component-per-file
const app = createApp({
  components: {
    'page-view': PageView,
  },
  head: {
    title: 'Online game & narrative design team collaboration tool',
    titleTemplate: '%s | Arcweave',
    meta: [
      {
        name: 'og:title',
        property: 'og:title',
        content: 'Online game & narrative design team collaboration tool',
      },
      {
        name: 'og:image',
        property: 'og:image',
        content: `${window.location.origin}/images/og-image.jpg`,
      },
      {
        name: 'twitter:image',
        property: 'twitter:image',
        content: `${window.location.origin}/images/og-image.jpg`,
      },
      {
        name: 'og:url',
        property: 'og:url',
        content: window.location.origin + window.location.pathname,
      },
    ],
  },
});

const head = createHead({});
app.mixin(VueHeadMixin);
app.config.compatConfig = { MODE: 2 };
app.config.compilerOptions.whitespace = 'preserve';

// Install Vue plugins
app.use(VueProgressiveImage);
app.use(head);
app.use(Vddl);
app.use(FloatingVue);
app.use(VueQueryPlugin, {
  queryClient,
});
QueryObservers.init();

// VueGtag sets up Google Analytics to track every SPA page using vue-router
app.use(VueGtag, {
  config: { id: 'G-8ED3R6TVBK' },
});

store.dispatch('initialize').then(() => {
  // Get the server time
  store.dispatch('getServerTime');
  // store.dispatch('loadWorkspace');
  app.use(router);
  app.use(store);
  app.mount('#app');
});

// Echo and socket.io setup
window.io = io;
window.echo = new Echo({
  broadcaster: 'socket.io',
  host: `${window.location.hostname}:${process.env.MIX_ECHO_SERVER_PORT}`,
});

// Event listeners for online/offline events
window.addEventListener('online', () => {
  store.commit('user/status/setIsOffline', { value: false });
});

window.addEventListener('offline', () => {
  store.commit('user/status/setIsOffline', { value: true });
});

// Vue 3 directive for touch events
app.directive('touch', {
  beforeMount(el, binding) {
    if (typeof binding.value === 'function') {
      let events = {
        singletap: true,
        doubletap: true,
        press: true,
        pan: true,
      };
      if (Object.keys(binding.modifiers).length > 0) {
        events = binding.modifiers;
      }

      const mc = propagating(new Hammer.Manager(el));
      const eventNames = [];
      if (events.doubletap) {
        // Tap recognizer with minimal 2 taps
        mc.add(new Hammer.Tap({ event: 'doubletap', taps: 2 }));
        eventNames.push('doubletap');
      }

      if (events.singletap) {
        // Single tap recognizer
        mc.add(new Hammer.Tap({ event: 'singletap' }));
        eventNames.push('singletap');
      }
      if (events.press) {
        // Press recognizer
        mc.add(new Hammer.Press({ event: 'press', time: 800 }));
        eventNames.push('press');
      }

      if (events.singletap && events.doubletap) {
        // we want to recognize this simulatenous, so a quadrupletap will be detected even while a tap has been recognized.
        mc.get('doubletap').recognizeWith('singletap');
        // we only want to trigger a tap, when we don't have detected a doubletap
        mc.get('singletap').requireFailure('doubletap');
      }

      mc.on(eventNames.join(' '), binding.value);
    }
  },
});

// Service Worker registration
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/service-worker.js')
      .then(registration => {
        console.log('SW registered: ', registration);
      })
      .catch(registrationError => {
        console.log('SW registration failed: ', registrationError);
      });
  });
}
